// AnmeldeAblauf.jsx — geführte Turnier-Anmeldung für Vereine (25.10.2026, Stufe 2 + 3)
//
// Nach dem Muster ~/Documents/TDA-Muster/Muster_Vereinsanmeldung_Einfach.html:
// Checkliste „Was du brauchst“, dann vier Schritte mit Fortschritt
//   1 Sportler · 2 Klassen · 3 Prüfen & Startgeld · 4 Fertig
// ?-Hilfen am Feld (Antippen zeigt Erklärung und Beispiel, per Tastatur
// bedienbar), Hilfe-Knopf mit dem Kontakt des Veranstalters aus der
// Ausschreibung und dem TDA-Kontakt, große Ziele am Handy, hell und dunkel.
//
// Klassen (Stufe 2): erst die Disziplin in einfachen Worten, dann nur die
// wirklich passenden Klassen mit deutschem Namen (lib/klassenEinfach.js),
// Code klein hinten, „Alle Klassen anzeigen“ als Ausweg. Am Turniertag schon
// gestartete Klassen bleiben ausgegraut (Server: utils/kategorieStart.js).
// Duo/Team: Partner aus der Liste der eigenen Sportler, Teamname freiwillig
// mit Vorschlag.
//
// Gespeichert wird über dieselbe Route wie im bisherigen Anmeldefenster
// (POST /api/anmeldungen, ohne is_update): Bestehendes bleibt, Neues kommt
// dazu. Abmelden geht weiter unter „Anmeldungen“. Gerechnet wird das
// Startgeld nur auf dem Server (POST /api/anmeldungen/gebuehr-vorschau).
//
// Gemessen wird je Schritt „erreicht“ und „abgeschlossen“ (POST
// /api/anmelde-schritte), ohne personenbezogene Inhalte.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { gebuehrVorschau, euro } from '../../lib/gebuehrVorschau';
import { alterAmStichtag } from '../../lib/divisionsFilter';
import {
  DISZIPLINEN, disziplinInfo, deutscherName, einfacheKlassen, klassenText, teamArt, warumNichtAlle,
} from '../../lib/klassenEinfach';
import AnmeldeBestaetigung from '../../pages/Verein/AnmeldeBestaetigung';
import './AnmeldeAblauf.css';

const NAMEN = ['Sportler', 'Klassen', 'Prüfen & Startgeld', 'Fertig'];

const ERFAHRUNG = { 'Beginner': 'Anfänger', 'Intermediate': 'Fortgeschritten', 'Advanced': 'Erfahren', 'Black Belt': 'Schwarzgurt' };
const geschlechtText = (g, alter) => {
  const k = String(g || '').toLowerCase();
  if (k.startsWith('w') || k === 'f') return alter !== null && alter < 18 ? 'Mädchen' : 'Frau';
  if (k.startsWith('m')) return alter !== null && alter < 18 ? 'Junge' : 'Mann';
  return 'Geschlecht fehlt';
};
const wkId = (a) => Number(a.wettkaempfer_id || a.id);
const kg = (g) => parseFloat(g).toLocaleString('de-DE', { maximumFractionDigits: 1 });
const vollerName = (a) => `${a?.vorname || ''} ${a?.nachname || ''}`.trim();
const datumText = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  return m ? `${m[3]}.${m[2]}.${m[1]}` : '';
};

// ?-Hilfen: [Titel, Erklärung, Beispiel]
const HILFE = {
  schluss: ['Bis wann?', 'Bis zu diesem Zeitpunkt kannst du anmelden und Klassen dazunehmen. Abmelden geht danach unter „Anmeldungen“ im Vereinsbereich.', ''],
  startgeld: ['Startgeld', 'Kosten je Start. Der erste Start eines Sportlers kostet am meisten, jeder weitere weniger. Ein Duo oder Team zahlt einmal je Team, aufgeteilt auf die Mitglieder.', 'Lena: 2 Starts = erster Start + zweiter Start'],
  sportler: ['Sportler', 'Jeder Sportler wird nur einmal angelegt und steht dann bei jedem Turnier zur Wahl. Für die Klassen zählen Alter am Turniertag, Geschlecht, Gewicht und Gurt.', 'Lena Muster, 03.05.2014, 38 kg, Gurt Weiß'],
  gewicht: ['Gewicht', 'Ungefähr reicht. Damit zeigen wir nur passende Gewichtsklassen. Ohne Gewicht gibt es keine Kampfklasse. Am Turniertag wird gewogen.', '38 kg'],
  klasse: ['Startklasse', 'Eine Gruppe, in der gleich alte, gleich schwere und gleich erfahrene Sportler gegeneinander antreten. Wir zeigen zuerst nur Klassen, die genau passen.', 'Leichtkontakt · Mädchen · 6–12 Jahre · 38–42 kg'],
  alter: ['Alter am Turniertag', 'Es zählt das Alter am Stichtag des Veranstalters, meist der Turniertag, nicht das heutige.', 'Geboren am 03.05.2014, Turnier am 13.03.2027: 12 Jahre'],
  erfahrung: ['Erfahrung', 'Ergibt sich aus dem Gurt: Weiß bis Orange Anfänger, Grün und Blau Fortgeschritten, Rot und Braun Erfahren, Schwarz Schwarzgurt. Anfänger sehen hier keine Klassen für Fortgeschrittene.', 'Gurt Grün: Fortgeschritten'],
  duo: ['Zu zweit oder im Team', 'Zwei oder mehr Sportler deines Vereins treten zusammen an. Wähle die Partner aus der Liste; sie werden in dieser Klasse mit angemeldet.', 'Lena und Tim zeigen zusammen Selbstverteidigung.'],
  teamname: ['Teamname', 'Freiwillig. So steht das Team in der Startliste. Leer lassen: wir nehmen die Vornamen.', 'Lena & Tim'],
  bezahlen: ['Bezahlen', 'Das Startgeld überweist du an den Veranstalter. Die Rechnung kommt per E-Mail und steht unter „Anmeldungen“.', ''],
};

function Frage({ id, offen, umschalten }) {
  const h = HILFE[id];
  return (
    <button type="button" className="aa-q" aria-label={`Hilfe: ${h[0]}`} aria-expanded={offen === id}
      aria-controls={`aa-h-${id}`} onClick={() => umschalten(id)}
      onKeyDown={(e) => { if (e.key === 'Escape' && offen === id) { e.stopPropagation(); umschalten(id); } }}>?</button>
  );
}
function HilfeBox({ id, offen }) {
  const h = HILFE[id];
  if (offen !== id) return null;
  return (
    <div className="aa-hilfe" id={`aa-h-${id}`} role="note">
      <b>{h[0]}</b>{h[1]}
      {h[2] && <div className="aa-bsp"><strong>Beispiel: </strong>{h[2]}</div>}
    </div>
  );
}

/** Zähler je Schritt (fire and forget, wartet auf nichts). */
function zaehlen(headers, turnierId, schritt, art) {
  try {
    fetch('/api/anmelde-schritte', {
      method: 'POST', keepalive: true,
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ turnier_id: turnierId, schritt, art }),
    }).catch(() => {});
  } catch { /* Zähler ist Beiwerk */ }
}

const entwurfSchluessel = (tid) => `aa-entwurf-${tid}`;
function entwurfLesen(tid) {
  try { return JSON.parse(sessionStorage.getItem(entwurfSchluessel(tid)) || 'null'); } catch { return null; }
}
function entwurfSchreiben(tid, wert) {
  try {
    if (wert) sessionStorage.setItem(entwurfSchluessel(tid), JSON.stringify(wert));
    else sessionStorage.removeItem(entwurfSchluessel(tid));
  } catch { /* privates Fenster: dann ohne Entwurf */ }
}

/**
 * @param {object} props
 * @param {object} props.turnier         { turnier_id, name, datum, ort, anmeldung }
 * @param {object[]} props.athletes      Sportler des Vereins
 * @param {object[]} props.anmeldungen   Anmeldungen des Vereins (alle Turniere)
 * @param {number} props.vereinsId
 * @param {object} props.headers         { Authorization }
 * @param {() => void} props.onSportlerAnlegen
 * @param {(a:object) => void} props.onSportlerBearbeiten
 * @param {() => void} props.onDatei     Sammelanmeldung per Datei
 * @param {() => Promise<void>} props.onGespeichert  lädt die Anmeldungen neu
 * @param {(ziel?:string) => void} props.onSchliessen
 * @param {() => void} props.onAusfuehrlich  bisheriges Anmeldefenster
 */
export default function AnmeldeAblauf({
  turnier, athletes, anmeldungen, vereinsId, headers,
  onSportlerAnlegen, onSportlerBearbeiten, onDatei, onGespeichert, onSchliessen, onAusfuehrlich,
}) {
  const tid = Number(turnier.turnier_id || turnier.id);
  const entwurf = useMemo(() => entwurfLesen(tid), [tid]);

  const [schritt, setSchritt] = useState(0);
  const [auswahl, setAuswahl] = useState(() => entwurf?.auswahl || []);
  const [aktiv, setAktiv] = useState(0);
  const [wahl, setWahl] = useState(() => entwurf?.wahl || {});        // { wkId: [code] } nur NEUE
  const [teams, setTeams] = useState(() => entwurf?.teams || {});     // { 'wkId__code': { name, mit:[wkId], von? } }
  const [disz, setDisz] = useState({});                               // { wkId: disziplinId }
  const [alleZeigen, setAlleZeigen] = useState(false);
  const [suche, setSuche] = useState('');

  const [info, setInfo] = useState(null);
  const [divisionen, setDivisionen] = useState(null);
  const [stichtag, setStichtag] = useState(null);
  const [ladeFehler, setLadeFehler] = useState('');
  const [vorschau, setVorschau] = useState(null);
  const [zahlung, setZahlung] = useState(null);
  const [ok, setOk] = useState(false);
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState(null);       // { text, details[] }
  const [gesendetIds, setGesendetIds] = useState([]);

  const [hilfe, setHilfe] = useState(null);
  const [panel, setPanel] = useState(false);
  const umschalten = (id) => setHilfe((h) => (h === id ? null : id));

  // ── Laden ────────────────────────────────────────────────────────────────
  useEffect(() => {
    let lebt = true;
    (async () => {
      try {
        const [ri, rd] = await Promise.all([
          fetch(`/api/turniere/public/${tid}/info`),
          fetch(`/api/divisionen?turnierId=${tid}&limit=2000&nurFreigegeben=1`, { headers }),
        ]);
        const di = await ri.json().catch(() => null);
        const dd = await rd.json().catch(() => null);
        if (!lebt) return;
        if (ri.ok && di?.data) { setInfo(di.data); setStichtag((s) => s || di.data.alters_stichtag || null); }
        if (rd.ok && dd) {
          setDivisionen(Array.isArray(dd.data) ? dd.data : (Array.isArray(dd) ? dd : []));
          if (dd.altersStichtag) setStichtag(dd.altersStichtag);
        } else setLadeFehler('Die Startklassen ließen sich nicht laden. Bitte die Seite neu laden.');
      } catch {
        if (lebt) setLadeFehler('Keine Verbindung zum Server. Bitte die Seite neu laden.');
      }
    })();
    return () => { lebt = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- einmal je Turnier; headers ändert sich nicht
  }, [tid]);

  // Ein Hilfe-Eingang im Ablauf: die Chat-Blase des Assistenten (KiAssistent)
  // läge sonst über der unteren Leiste und am Handy über „Weiter“.
  useEffect(() => {
    document.body.classList.add('aa-aktiv');
    return () => document.body.classList.remove('aa-aktiv');
  }, []);

  // ── Zähler: erreicht beim Betreten, je Sitzung einmal ─────────────────────
  const gezaehlt = useRef(new Set());
  useEffect(() => {
    if (gezaehlt.current.has(schritt)) return;
    gezaehlt.current.add(schritt);
    zaehlen(headers, tid, schritt, 'erreicht');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- nur beim Schrittwechsel
  }, [schritt, tid]);

  const geh = (ziel) => {
    if (ziel > schritt) zaehlen(headers, tid, schritt, 'abgeschlossen');
    setHilfe(null); setFehler(null); setAlleZeigen(false);
    setSchritt(ziel);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Entwurf merken (sessionStorage): Zurück und Neuladen verlieren nichts
  useEffect(() => {
    if (schritt >= 4) return;
    entwurfSchreiben(tid, { auswahl, wahl, teams });
  }, [tid, auswahl, wahl, teams, schritt]);

  // ── Abgeleitetes ─────────────────────────────────────────────────────────
  const sportler = useMemo(() => new Map(athletes.map((a) => [wkId(a), a])), [athletes]);
  // Auswahl auf vorhandene Sportler beschränken (gelöscht, anderer Verein)
  const auswahlDa = auswahl.filter((id) => sportler.has(id));

  // Neu angelegte Sportler gleich auswählen
  const bekannt = useRef(null);
  useEffect(() => {
    const ids = athletes.map(wkId);
    if (bekannt.current) {
      const neu = ids.filter((id) => !bekannt.current.has(id));
      if (neu.length) setAuswahl((a) => [...new Set([...a, ...neu])]);
    }
    bekannt.current = new Set(ids);
  }, [athletes]);

  const schonGemeldet = useMemo(() => {
    const m = {};
    for (const a of anmeldungen || []) {
      if (Number(a.turnier_id) !== tid || a.status === 'Storniert' || !a.division_code) continue;
      const id = Number(a.wettkaempfer_id || a.wettkampfer_id);
      (m[id] = m[id] || []).push(a.division_code);
    }
    return m;
  }, [anmeldungen, tid]);

  const klassenJe = useMemo(() => {
    const m = new Map();
    if (!divisionen) return m;
    for (const id of new Set([...auswahlDa, ...athletes.map(wkId)])) {
      const a = sportler.get(id);
      if (a) m.set(id, einfacheKlassen(divisionen, a, stichtag));
    }
    return m;
  }, [divisionen, stichtag, athletes, auswahlDa, sportler]);

  const divNach = useMemo(() => new Map((divisionen || []).map((d) => [d.Division_Code, d])), [divisionen]);

  const alterVon = (a) => alterAmStichtag(a?.geburtsdatum, stichtag);
  const beschreibung = (a) => {
    const alt = alterVon(a);
    const teile = [
      alt !== null ? `${alt} Jahre am Turniertag` : 'Geburtsdatum fehlt',
      geschlechtText(a.geschlecht, alt),
      parseFloat(a.gewicht) > 0 ? `${kg(a.gewicht)} kg` : null,
      a.gurtfarbe ? `Gurt ${a.gurtfarbe}${a.skill_level ? ` (${ERFAHRUNG[a.skill_level] || a.skill_level})` : ''}` : null,
    ];
    return teile.filter(Boolean).join(' · ');
  };

  const teamSchluessel = (id, code) => `${id}__${code}`;
  const teamVon = (id, code) => teams[teamSchluessel(id, code)];
  const teamVorschlag = (ids) => ids.map((x) => sportler.get(x)?.vorname).filter(Boolean).join(' & ');
  const teamNameFuer = (id, code) => {
    const t = teamVon(id, code);
    if (!t) return '';
    const kopf = t.von ? teamVon(t.von, code) : t;
    const mitglieder = t.von ? [t.von, ...(kopf?.mit || [])] : [id, ...(t.mit || [])];
    return (kopf?.name || '').trim() || teamVorschlag([...new Set(mitglieder)]);
  };

  // ── Startgeld (Server) ───────────────────────────────────────────────────
  const eintraege = useMemo(() => Object.entries(wahl)
    .filter(([id, codes]) => codes.length && sportler.has(Number(id)))
    .map(([id, codes]) => ({
      wettkaempfer_id: Number(id),
      divisionen: codes,
      teams: Object.fromEntries(codes.filter((c) => teamArt(divNach.get(c))).map((c) => [c, teamNameFuer(Number(id), c)])),
    })),
  // eslint-disable-next-line react-hooks/exhaustive-deps -- teamNameFuer liest teams/sportler, die hier stehen
  [wahl, teams, sportler, divNach]);

  useEffect(() => {
    if (!eintraege.length) { setVorschau(null); return undefined; }
    let aktuell = true;
    const t = setTimeout(async () => {
      try {
        const d = await gebuehrVorschau({ turnier_id: tid, verein_id: vereinsId, eintraege });
        if (aktuell) setVorschau(d);
      } catch { if (aktuell) setVorschau(null); }
    }, 250);
    return () => { aktuell = false; clearTimeout(t); };
  }, [eintraege, tid, vereinsId]);

  const betrag = (id, code) => vorschau?.zeilen?.find((z) => Number(z.wettkaempfer_id) === Number(id) && z.division_code === code);
  const summeVon = (id) => (vorschau?.zeilen || []).filter((z) => Number(z.wettkaempfer_id) === Number(id))
    .reduce((s, z) => s + (Number(z.betrag) || 0), 0);
  const startsGesamt = eintraege.reduce((s, e) => s + e.divisionen.length, 0);

  // ── Zahlung (Schritt 3) ──────────────────────────────────────────────────
  useEffect(() => {
    if (schritt !== 3 || zahlung) return;
    (async () => {
      try {
        const r = await fetch(`/api/zahlung/verein/${tid}/${vereinsId}`, { headers });
        const d = await r.json().catch(() => ({}));
        setZahlung(r.ok && d.success ? d : { fehler: true });
      } catch { setZahlung({ fehler: true }); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schritt]);

  // ── Auswahl ändern ───────────────────────────────────────────────────────
  const sportlerUmschalten = (id) => setAuswahl((a) => (a.includes(id) ? a.filter((x) => x !== id) : [...a, id]));

  const codeSetzen = (id, code, an) => setWahl((w) => {
    const alt = w[id] || [];
    const neu = an ? [...new Set([...alt, code])] : alt.filter((c) => c !== code);
    return { ...w, [id]: neu };
  });

  // Partner einer Team-Klasse setzen: Partner bekommt dieselbe Klasse (gespiegelt)
  const partnerSetzen = (id, code, mit) => {
    const alt = teamVon(id, code)?.mit || [];
    setTeams((t) => {
      const n = { ...t, [teamSchluessel(id, code)]: { ...(t[teamSchluessel(id, code)] || {}), mit } };
      for (const p of alt) if (!mit.includes(p) && n[teamSchluessel(p, code)]?.von === id) delete n[teamSchluessel(p, code)];
      for (const p of mit) n[teamSchluessel(p, code)] = { von: id };
      return n;
    });
    setWahl((w) => {
      const n = { ...w };
      for (const p of alt) if (!mit.includes(p)) n[p] = (n[p] || []).filter((c) => c !== code);
      for (const p of mit) n[p] = [...new Set([...(n[p] || []), code])];
      return n;
    });
    setAuswahl((a) => [...new Set([...a, ...mit])]);
  };

  const klasseUmschalten = (id, d, an) => {
    const code = d.Division_Code;
    if (an && d.gestartet) return;
    const t = teamVon(id, code);
    if (!an && t) {
      // Abwählen: beim Kopf das ganze Team, beim gespiegelten nur sich selbst
      if (t.von) partnerSetzen(t.von, code, (teamVon(t.von, code)?.mit || []).filter((x) => x !== id));
      else { partnerSetzen(id, code, []); setTeams((x) => { const n = { ...x }; delete n[teamSchluessel(id, code)]; return n; }); }
    }
    codeSetzen(id, code, an);
  };

  // ── Absenden ─────────────────────────────────────────────────────────────
  const absenden = async () => {
    setSendet(true); setFehler(null);
    const ids = eintraege.map((e) => e.wettkaempfer_id);
    const teamDaten = [];
    for (const e of eintraege) {
      for (const code of e.divisionen) {
        if (!teamArt(divNach.get(code))) continue;
        const t = teamVon(e.wettkaempfer_id, code);
        const kopf = t?.von ? t.von : e.wettkaempfer_id;
        const alle = [kopf, ...((teamVon(kopf, code)?.mit) || [])];
        teamDaten.push({
          wettkaempfer_id: e.wettkaempfer_id, division_code: code,
          team_name: teamNameFuer(e.wettkaempfer_id, code),
          mitglieder: alle.filter((x) => x !== e.wettkaempfer_id).map((x) => vollerName(sportler.get(x))).filter(Boolean),
        });
      }
    }
    try {
      const r = await fetch('/api/anmeldungen', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          turnier_id: tid, verein_id: vereinsId, wettkampfer_ids: ids,
          wettkampfer_divisionen: eintraege.map((e) => ({ wettkaempfer_id: e.wettkaempfer_id, divisionen: e.divisionen })),
          anmelde_datum: new Date().toISOString(),
          ...(teamDaten.length ? { team_daten: teamDaten } : {}),
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.success === false) {
        setFehler({
          text: d.message || d.error || `Nicht gespeichert (Fehler ${r.status}).`,
          details: Array.isArray(d.details) ? d.details : [],
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      zaehlen(headers, tid, 3, 'abgeschlossen');
      entwurfSchreiben(tid, null);
      setGesendetIds(ids);
      await onGespeichert();
      setSchritt(4);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      setFehler({ text: 'Keine Verbindung zum Server. Deine Auswahl ist noch da, bitte gleich noch einmal versuchen.', details: [] });
    } finally { setSendet(false); }
  };

  const vonVorn = () => {
    setAuswahl([]); setWahl({}); setTeams({}); setOk(false); setZahlung(null); setAktiv(0);
    gezaehlt.current = new Set([0]);
    setSchritt(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── Bausteine ────────────────────────────────────────────────────────────
  const anm = info?.anmeldung || turnier.anmeldung;
  const geb = info?.gebuehren;
  const kontaktMail = info?.kontakt_email;
  const kontaktTel = info?.kontakt_telefon;
  const veranstalter = info?.veranstalter_name || info?.veranstalter || 'Veranstalter';

  const fortschritt = schritt >= 1 && (
    <div className="aa-fort" aria-label={`Schritt ${schritt} von 4: ${NAMEN[schritt - 1]}`}>
      <div className="aa-fort-zeile"><b>Schritt {schritt} von 4: {NAMEN[schritt - 1]}</b></div>
      <div className="aa-balken" aria-hidden="true">
        {NAMEN.map((n, i) => <i key={n} className={i + 1 < schritt || schritt === 4 ? 'fertig' : i + 1 === schritt ? 'jetzt' : ''} />)}
      </div>
      <div className="aa-schritt-namen" aria-hidden="true">
        {NAMEN.map((n, i) => (
          <span key={n} className={i + 1 < schritt || schritt === 4 ? 'fertig' : i + 1 === schritt ? 'jetzt' : ''}>
            {i + 1 < schritt || schritt === 4 ? '✓ ' : ''}{n}
          </span>
        ))}
      </div>
    </div>
  );

  const unten = (infoText, knopf, { zurueck, weiter, aus = false, zurueckText = 'Zurück' }) => (
    <div className="aa-unten">
      <div className="aa-unten-in">
        <div className="aa-unten-info">{infoText}</div>
        {zurueck && <button type="button" className="aa-knopf zweit" onClick={zurueck}>{zurueckText}</button>}
        <button type="button" className="aa-knopf" onClick={weiter} disabled={aus}>{knopf}</button>
      </div>
    </div>
  );

  const hilfeKnopf = (
    <>
      <button type="button" className={`aa-hilfeknopf ${schritt === 4 ? 'aa-ohne-leiste' : ''}`} aria-expanded={panel} aria-controls="aa-hilfepanel"
        onClick={() => setPanel((p) => !p)}>? Hilfe</button>
      {panel && (
        <div className={`aa-hilfepanel ${schritt === 4 ? 'aa-ohne-leiste' : ''}`} id="aa-hilfepanel" role="dialog" aria-label="Hilfe"
          onKeyDown={(e) => { if (e.key === 'Escape') setPanel(false); }}>
          <div className="aa-hp-kopf"><b>Kommst du nicht weiter?</b>
            <button type="button" className="aa-x" aria-label="Hilfe schließen" onClick={() => setPanel(false)}>✕</button></div>
          <p>Sag einfach, bei welchem Schritt du bist{schritt >= 1 && schritt <= 4 ? ` (oben steht „Schritt ${schritt} von 4“)` : ''}.</p>
          <div className="aa-hp-titel">Fragen zum Turnier: {veranstalter}</div>
          {kontaktTel && <a className="aa-k" href={`tel:${String(kontaktTel).replace(/\s/g, '')}`}>📞 Anrufen: {kontaktTel}</a>}
          {kontaktMail && <a className="aa-k" href={`mailto:${kontaktMail}?subject=${encodeURIComponent(`Anmeldung ${turnier.name}`)}`}>✉️ E-Mail: {kontaktMail}</a>}
          {!kontaktTel && !kontaktMail && <p className="aa-klein">Der Veranstalter hat in der Ausschreibung keinen Kontakt eingetragen.</p>}
          <a className="aa-k" href={`/event/${tid}`} target="_blank" rel="noreferrer">📄 Ausschreibung ansehen</a>
          <div className="aa-hp-titel">Fragen zur Anmeldung hier</div>
          {typeof document !== 'undefined' && document.getElementById('vc-btn') && (
            <button type="button" className="aa-k" onClick={() => { setPanel(false); document.getElementById('vc-btn')?.click(); }}>💬 Chat mit TDA</button>
          )}
          <a className="aa-k" href="/kontakt" target="_blank" rel="noreferrer">✉️ TDA-Kontakt</a>
        </div>
      )}
    </>
  );

  // ── Schritt 0: Checkliste ────────────────────────────────────────────────
  const s0 = () => (
    <main className="aa-main">
      <h1>Sportler für den {turnier.name} anmelden</h1>
      <p className="aa-lead">In 4 kurzen Schritten. Du kannst jederzeit zurück, deine Auswahl bleibt erhalten.</p>
      <div className="aa-schritte4">
        {NAMEN.map((n, i) => <div key={n}><b>{i + 1}</b>{n}</div>)}
      </div>
      <div className="aa-karte">
        <h3>Das brauchst du</h3>
        <ul className="aa-liste">
          <li><span className="aa-haken">✓</span><div><b>Je Sportler: Name, Geburtsdatum, Gewicht, Gurtfarbe</b>
            <span>{athletes.length
              ? `${athletes.length} ${athletes.length === 1 ? 'Sportler ist' : 'Sportler sind'} schon eingetragen. Fehlt jemand, legst du ihn in Schritt 1 an.`
              : 'Noch ist kein Sportler eingetragen. Das geht in Schritt 1, je Sportler etwa eine Minute.'}</span></div></li>
          <li><span className="aa-haken">✓</span><div><b>Was jeder mitmachen will</b>
            <span>Zum Beispiel Formen oder Leichtkontakt. Wir zeigen danach nur die Klassen, die passen.</span></div></li>
          <li><span className="aa-haken">✓</span><div><b>Für Duo oder Team: alle Partner aus deinem Verein</b>
            <span>Sie müssen als Sportler eingetragen sein, dann wählst du sie aus einer Liste.</span></div></li>
          <li><span className="aa-haken">✓</span><div><b>Viele Sportler?</b>
            <span>Du kannst auch eine Liste als Datei hochladen. <button type="button" className="aa-link" onClick={onDatei}>Per Datei anmelden</button></span></div></li>
        </ul>
      </div>
      <div className="aa-karte">
        <div className="aa-zeile-q"><b>Anmeldung möglich:</b>&nbsp;{anm ? `${anm.text}${anm.zusatz ? `, ${anm.zusatz}` : ''}` : 'siehe Ausschreibung'} <Frage id="schluss" offen={hilfe} umschalten={umschalten} /></div>
        <HilfeBox id="schluss" offen={hilfe} />
        <div className="aa-zeile-q"><b>Startgeld:</b>&nbsp;{geb
          ? `${euro(geb.einzel_erste)} für den ersten Start${geb.einzel_zweite != null ? `, ${euro(geb.einzel_zweite)} für den zweiten` : ''}${geb.einzel_weitere != null ? `, ab dem dritten ${euro(geb.einzel_weitere)}` : ''}${geb.team ? `; Duo/Team ${euro(geb.team)} je Team` : ''}`
          : 'wird beim Prüfen berechnet'} <Frage id="startgeld" offen={hilfe} umschalten={umschalten} /></div>
        <HilfeBox id="startgeld" offen={hilfe} />
      </div>
      {ladeFehler && <div className="aa-warnung" role="alert"><span>⚠</span><div>{ladeFehler}</div></div>}
      {unten('', 'Los geht’s', { zurueck: () => onSchliessen(), zurueckText: 'Zurück zum Vereinsbereich', weiter: () => geh(1) })}
    </main>
  );

  // ── Schritt 1: Sportler ──────────────────────────────────────────────────
  const s1 = () => {
    const liste = athletes
      .filter((a) => !suche || vollerName(a).toLowerCase().includes(suche.toLowerCase()))
      .sort((a, b) => `${a.nachname} ${a.vorname}`.localeCompare(`${b.nachname} ${b.vorname}`, 'de'));
    return (
      <main className="aa-main">
        <h1>Wer startet?</h1>
        <p className="aa-lead">Tippe alle Sportler an, die beim {turnier.name} starten. <Frage id="sportler" offen={hilfe} umschalten={umschalten} /></p>
        <HilfeBox id="sportler" offen={hilfe} />
        {athletes.length === 0 ? (
          <div className="aa-karte aa-leer">
            <h3>Noch kein Sportler eingetragen</h3>
            <p>Trag jeden Sportler einmal ein. Beim nächsten Turnier ist er schon da.</p>
            <button type="button" className="aa-knopf breit" onClick={onSportlerAnlegen}>＋ Ersten Sportler eintragen</button>
          </div>
        ) : (
          <div className="aa-karte">
            <div className="aa-karte-kopf">
              <h3>Deine Sportler ({athletes.length})</h3>
              {athletes.length > 8 && (
                <input className="aa-suche" type="search" placeholder="Name suchen" aria-label="Sportler suchen"
                  value={suche} onChange={(e) => setSuche(e.target.value)} />
              )}
            </div>
            <div className="aa-sp-liste">
              {liste.map((a) => {
                const id = wkId(a);
                const an = auswahlDa.includes(id);
                const gemeldet = schonGemeldet[id]?.length || 0;
                const ohneGewicht = !(parseFloat(a.gewicht) > 0);
                return (
                  <div key={id} className={`aa-sp ${an ? 'an' : ''}`}>
                    <label className="aa-sp-wahl">
                      <input type="checkbox" checked={an} onChange={() => sportlerUmschalten(id)} />
                      <span className="aa-av" aria-hidden="true">{an ? '✓' : `${(a.vorname || '?')[0]}${(a.nachname || '?')[0]}`}</span>
                      <span className="aa-sp-text">
                        <span className="aa-sp-n">{vollerName(a)}</span>
                        <span className="aa-sp-d">{beschreibung(a)}</span>
                        <span className="aa-chips">
                          {gemeldet > 0 && <span className="aa-chip ok">schon angemeldet: {gemeldet} {gemeldet === 1 ? 'Start' : 'Starts'}</span>}
                          {ohneGewicht && <span className="aa-chip warn">Gewicht fehlt: keine Kampfklasse</span>}
                        </span>
                      </span>
                    </label>
                    <button type="button" className="aa-knopf zweit mini" onClick={() => onSportlerBearbeiten(a)}>Ändern</button>
                  </div>
                );
              })}
            </div>
            <button type="button" className="aa-knopf zweit breit" onClick={onSportlerAnlegen}>＋ Weiteren Sportler eintragen</button>
            <p className="aa-mitte aa-klein">Viele Sportler? <button type="button" className="aa-link" onClick={onDatei}>Per Datei anmelden</button></p>
          </div>
        )}
        {unten(<><b>{auswahlDa.length} {auswahlDa.length === 1 ? 'Sportler' : 'Sportler'}</b> ausgewählt{auswahlDa.length === 0 ? ': bitte mindestens einen antippen' : ''}</>,
          'Weiter zu den Klassen', {
            zurueck: () => geh(0),
            weiter: () => { setAktiv(0); geh(2); },
            aus: auswahlDa.length === 0,
          })}
      </main>
    );
  };

  // ── Schritt 2: Klassen ───────────────────────────────────────────────────
  const klassenKarte = (id, d, { neuGewaehlt, gemeldet }) => {
    const code = d.Division_Code;
    const art = teamArt(d);
    const gestartet = d.gestartet && !neuGewaehlt;
    const t = teamVon(id, code);
    const b = betrag(id, code);
    const partnerMoeglich = art ? athletes.filter((x) => wkId(x) !== id
      && (klassenJe.get(wkId(x))?.passend || []).some((p) => p.Division_Code === code)) : [];
    return (
      <div key={code} className={`aa-klasse ${neuGewaehlt || gemeldet ? 'an' : ''} ${gestartet ? 'aus' : ''}`}>
        <label className="aa-klasse-wahl">
          <input type="checkbox" checked={neuGewaehlt || gemeldet} disabled={gemeldet || gestartet || !!t?.von}
            onChange={(e) => klasseUmschalten(id, d, e.target.checked)} />
          <span className="aa-kbox" aria-hidden="true">{neuGewaehlt || gemeldet ? '✓' : ''}</span>
          <span className="aa-klasse-text">
            <span className="aa-kn">{deutscherName(d)}</span>
            <span className="aa-kd">{klassenText(d)}</span>
            {gemeldet && <span className="aa-chip ok">schon angemeldet</span>}
            {gestartet && <span className="aa-chip">schon gestartet{d.gestartet_grund ? ` (${d.gestartet_grund})` : ''}, Anmeldung nicht mehr möglich</span>}
            {t?.von && <span className="aa-chip">als Partner von {sportler.get(t.von)?.vorname} gewählt</span>}
            <span className="aa-code">{code}</span>
          </span>
          {neuGewaehlt && b && <span className="aa-preis">{b.im_teampreis ? 'im Teampreis' : euro(b.betrag)}</span>}
        </label>
        {art && neuGewaehlt && !t?.von && (
          <div className="aa-partner">
            {partnerMoeglich.length === 0 ? (
              <p className="aa-klein">Kein anderer Sportler deines Vereins passt in diese Klasse.{' '}
                <button type="button" className="aa-link" onClick={onSportlerAnlegen}>Partner als Sportler eintragen</button></p>
            ) : art === 'duo' ? (
              <label className="aa-feld">
                <span className="aa-l">Mit wem?</span>
                <select value={t?.mit?.[0] ?? ''} onChange={(e) => partnerSetzen(id, code, e.target.value ? [Number(e.target.value)] : [])}>
                  <option value="">Partner wählen …</option>
                  {partnerMoeglich.map((x) => <option key={wkId(x)} value={wkId(x)}>{vollerName(x)} (wird mit angemeldet)</option>)}
                </select>
              </label>
            ) : (
              <fieldset className="aa-feld">
                <legend className="aa-l">Wer ist noch im Team?</legend>
                {partnerMoeglich.map((x) => {
                  const xid = wkId(x);
                  const drin = (t?.mit || []).includes(xid);
                  return (
                    <label key={xid} className="aa-check-zeile">
                      <input type="checkbox" checked={drin}
                        onChange={() => partnerSetzen(id, code, drin ? t.mit.filter((y) => y !== xid) : [...(t?.mit || []), xid])} />
                      {vollerName(x)}
                    </label>
                  );
                })}
              </fieldset>
            )}
            {(t?.mit || []).length > 0 && (
              <label className="aa-feld">
                <span className="aa-l">Teamname <span className="aa-frei">(freiwillig)</span> <Frage id="teamname" offen={hilfe} umschalten={umschalten} /></span>
                <HilfeBox id="teamname" offen={hilfe} />
                <input type="text" value={t?.name || ''} placeholder={teamVorschlag([id, ...(t?.mit || [])])} maxLength={120}
                  onChange={(e) => setTeams((x) => ({ ...x, [teamSchluessel(id, code)]: { ...(x[teamSchluessel(id, code)] || {}), name: e.target.value } }))} />
                <span className="aa-erkl">Leer lassen: das Team heißt „{teamVorschlag([id, ...(t?.mit || [])])}“.</span>
              </label>
            )}
            {art && neuGewaehlt && !(t?.mit || []).length && partnerMoeglich.length > 0 && (
              <p className="aa-klein aa-gelb">Bitte {art === 'duo' ? 'den Partner' : 'mindestens einen Partner'} wählen, sonst startest du allein in dieser Klasse.</p>
            )}
          </div>
        )}
      </div>
    );
  };

  const s2 = () => {
    if (!divisionen) {
      return (
        <main className="aa-main">
          <h1>Klassen werden geladen …</h1>
          {ladeFehler && <div className="aa-warnung" role="alert"><span>⚠</span><div>{ladeFehler}</div></div>}
          {unten('', 'Weiter', { zurueck: () => geh(1), weiter: () => {}, aus: true })}
        </main>
      );
    }
    const index = Math.min(aktiv, auswahlDa.length - 1);
    const id = auswahlDa[index];
    const a = sportler.get(id);
    if (!a) return null;
    const k = klassenJe.get(id) || { passend: [], einfach: {}, alle: {} };
    const quelle = alleZeigen ? k.alle : k.einfach;
    const gemeldetHier = schonGemeldet[id] || [];
    const neu = wahl[id] || [];
    const sichtbareDisz = DISZIPLINEN.map((x) => x.id).concat(['sonst'])
      .filter((x) => (k.alle[x] || []).length > 0);
    const aktDisz = disz[id] && sichtbareDisz.includes(disz[id]) ? disz[id] : sichtbareDisz[0];
    const zaehl = (x) => (k.alle[x] || []).filter((d) => neu.includes(d.Division_Code) || gemeldetHier.includes(d.Division_Code)).length;
    // Gewählte Klassen bleiben sichtbar, auch wenn sie nur unter „Alle“ stehen
    const liste = [...(quelle[aktDisz] || [])];
    for (const d of k.alle[aktDisz] || []) {
      if ((neu.includes(d.Division_Code) || gemeldetHier.includes(d.Division_Code)) && !liste.includes(d)) liste.push(d);
    }
    const angezeigt = Object.values(k.einfach).reduce((s, l) => s + l.length, 0);
    const warum = warumNichtAlle(divisionen, a, stichtag, angezeigt);
    const alt = alterVon(a);
    const letzter = index >= auswahlDa.length - 1;
    const naechster = sportler.get(auswahlDa[index + 1]);
    const ohneGewicht = !(parseFloat(a.gewicht) > 0);
    return (
      <main className="aa-main">
        <h1>Was macht {a.vorname} mit?</h1>
        <p className="aa-lead">
          Wir zeigen nur Klassen, die zu {a.vorname} passen: {alt !== null ? `${alt} Jahre am Turniertag` : 'Alter unbekannt'} <Frage id="alter" offen={hilfe} umschalten={umschalten} />,{' '}
          {geschlechtText(a.geschlecht, alt)}, {ohneGewicht ? 'ohne Gewicht' : `${kg(a.gewicht)} kg`},{' '}
          {ERFAHRUNG[a.skill_level] || 'Erfahrung unbekannt'} <Frage id="erfahrung" offen={hilfe} umschalten={umschalten} />.{' '}
          <button type="button" className="aa-link" onClick={() => onSportlerBearbeiten(a)}>Stimmt etwas nicht?</button>
        </p>
        <HilfeBox id="alter" offen={hilfe} /><HilfeBox id="erfahrung" offen={hilfe} />
        {auswahlDa.length > 1 && (
          <div className="aa-sp-tabs" role="tablist" aria-label="Sportler">
            {auswahlDa.map((x, i) => {
              const n = (wahl[x] || []).length;
              return (
                <button key={x} type="button" role="tab" aria-selected={i === index} className={`aa-sp-tab ${i === index ? 'an' : ''}`}
                  onClick={() => { setAktiv(i); setAlleZeigen(false); setHilfe(null); }}>
                  <b>{vollerName(sportler.get(x))}</b>
                  {n ? <span className="aa-chip ok">✓ {n} gewählt</span> : <span className="aa-chip warn">noch nichts gewählt</span>}
                </button>
              );
            })}
          </div>
        )}
        {ohneGewicht && (
          <div className="aa-tipp"><span>⚖️</span><div>Für {a.vorname} ist kein Gewicht eingetragen. Ohne Gewicht gibt es keine Kampfklasse.{' '}
            <button type="button" className="aa-link" onClick={() => onSportlerBearbeiten(a)}>Gewicht eintragen</button></div></div>
        )}
        <h2 className="aa-h2">Disziplin wählen</h2>
        <div className="aa-diszis">
          {sichtbareDisz.map((x) => {
            const inf = disziplinInfo(x);
            const z = zaehl(x);
            return (
              <button key={x} type="button" className={`aa-diszi ${x === aktDisz ? 'an' : ''}`} aria-pressed={x === aktDisz}
                onClick={() => {
                  setDisz((m) => ({ ...m, [id]: x })); setHilfe(null);
                  // Am Handy liegen die Klassen unter elf Kacheln: gleich hinführen
                  if (window.innerWidth < 600) setTimeout(() => document.getElementById('aa-klassen')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
                }}>
                <b>{inf.name}{z > 0 && <span className="aa-chip ok"> ✓ {z}</span>}</b>
                <small>{inf.text}</small>
              </button>
            );
          })}
        </div>
        {sichtbareDisz.length === 0 && (
          <div className="aa-warnung"><span>⚠</span><div>Für {a.vorname} gibt es bei diesem Turnier keine passende Klasse.
            Prüfe Geburtsdatum, Geschlecht, Gewicht und Gurt, oder frag den Veranstalter (Knopf „Hilfe“).</div></div>
        )}
        {aktDisz && (
          <div className="aa-karte" id="aa-klassen">
            <h3 className="aa-zeile-q">{disziplinInfo(aktDisz).name}: {liste.length} {alleZeigen ? '' : 'passende '}Klasse{liste.length === 1 ? '' : 'n'} <Frage id="klasse" offen={hilfe} umschalten={umschalten} />
              {liste.some((d) => teamArt(d)) && <span className="aa-q-text">Zu zweit oder im Team <Frage id="duo" offen={hilfe} umschalten={umschalten} /></span>}</h3>
            <HilfeBox id="klasse" offen={hilfe} /><HilfeBox id="duo" offen={hilfe} />
            {liste.length === 0 && !alleZeigen && (
              <p className="aa-klein">Keine Klasse passt genau. Unter „Alle Klassen anzeigen“ stehen weitere, in denen {a.vorname} starten darf (zum Beispiel eine Altersklasse höher).</p>
            )}
            {liste.map((d) => klassenKarte(id, d, {
              neuGewaehlt: neu.includes(d.Division_Code),
              gemeldet: gemeldetHier.includes(d.Division_Code),
            }))}
            <button type="button" className="aa-link" aria-pressed={alleZeigen} onClick={() => setAlleZeigen((x) => !x)}>
              {alleZeigen ? 'Nur passende Klassen zeigen' : `Alle Klassen anzeigen (${(k.alle[aktDisz] || []).length})`}
            </button>
            <details className="aa-warum">
              <summary>Warum sehe ich nicht alle {warum.gesamt} Klassen?</summary>
              <ul>
                {warum.alter > 0 && <li>{warum.alter} sind für eine andere Altersgruppe</li>}
                {warum.geschlecht > 0 && <li>{warum.geschlecht} sind nur für {String(a.geschlecht || '').toLowerCase().startsWith('w') ? 'Jungen und Männer' : 'Mädchen und Frauen'}</li>}
                {warum.erfahrung > 0 && <li>{warum.erfahrung} sind für eine andere Erfahrung (Gurt)</li>}
                {warum.gewicht > 0 && <li>{warum.gewicht} sind für eine andere Gewichtsklasse</li>}
                {warum.aehnlich > 0 && <li>{warum.aehnlich} wären möglich, passen aber nicht genau (eine Klasse höher, gleich lautend): „Alle Klassen anzeigen“</li>}
              </ul>
              <span className="aa-klein">Stimmt eine Angabe nicht? <button type="button" className="aa-link" onClick={() => onSportlerBearbeiten(a)}>{a.vorname} ändern</button></span>
            </details>
          </div>
        )}
        {onAusfuehrlich && (
          <p className="aa-klein aa-mitte">Lieber die ausführliche Liste mit Suche und Filtern? <button type="button" className="aa-link" onClick={onAusfuehrlich}>Ausführliche Ansicht</button></p>
        )}
        {unten(<>{a.vorname}: <b>{neu.length} {neu.length === 1 ? 'neuer Start' : 'neue Starts'}{vorschau ? ` · ${euro(summeVon(id))}` : ''}</b>{startsGesamt === 0 && letzter ? <><br />Bitte mindestens eine Klasse wählen</> : null}</>,
          letzter ? 'Weiter zum Prüfen' : `Weiter zu ${naechster?.vorname || 'nächstem'}`, {
            zurueck: () => { if (index > 0) { setAktiv(index - 1); setAlleZeigen(false); window.scrollTo({ top: 0 }); } else geh(1); },
            weiter: () => { if (letzter) geh(3); else { setAktiv(index + 1); setAlleZeigen(false); setHilfe(null); window.scrollTo({ top: 0, behavior: 'smooth' }); } },
            aus: letzter && startsGesamt === 0,
          })}
      </main>
    );
  };

  // ── Schritt 3: Prüfen & Startgeld ────────────────────────────────────────
  const kopieren = (text) => { try { navigator.clipboard?.writeText(String(text).replace(/\s+/g, ' ')); } catch { /* ohne Zwischenablage */ } };
  const s3 = () => {
    const bank = zahlung?.bank;
    const gesamt = vorschau?.summe;
    return (
      <main className="aa-main">
        <h1>Alles richtig?</h1>
        <p className="aa-lead">Bitte einmal durchsehen. {anm?.anmeldbar ? `Ändern und nachmelden geht danach weiter: ${anm.text}${anm.zusatz ? `, ${anm.zusatz}` : ''}.` : ''}</p>
        {fehler && (
          <div className="aa-warnung" role="alert"><span>⚠</span><div><b>Noch nicht angemeldet.</b> {fehler.text}
            {fehler.details.length > 1 && <ul>{fehler.details.map((x) => <li key={x}>{x}</li>)}</ul>}
            <div className="aa-klein">Deine Auswahl ist noch da. Ändern über „ändern“ oder frag über „Hilfe“.</div></div></div>
        )}
        {eintraege.map((e) => {
          const a = sportler.get(e.wettkaempfer_id);
          const pos = auswahlDa.indexOf(e.wettkaempfer_id);
          return (
            <div key={e.wettkaempfer_id} className="aa-karte">
              <h3 className="aa-karte-kopf">{vollerName(a)}
                <button type="button" className="aa-link" onClick={() => { setAktiv(Math.max(0, pos)); geh(2); }}>ändern</button></h3>
              {e.divisionen.map((code) => {
                const d = divNach.get(code) || { Division_Code: code };
                const b = betrag(e.wettkaempfer_id, code);
                const team = teamArt(d);
                const t = teamVon(e.wettkaempfer_id, code);
                const kopf = t?.von || e.wettkaempfer_id;
                const andere = [kopf, ...((teamVon(kopf, code)?.mit) || [])].filter((x) => x !== e.wettkaempfer_id);
                return (
                  <div key={code} className="aa-zeile">
                    <div><b>{deutscherName(d)}</b>
                      <div className="aa-w">{klassenText(d)}{team && andere.length ? ` · mit ${andere.map((x) => sportler.get(x)?.vorname).join(', ')} („${teamNameFuer(e.wettkaempfer_id, code)}“)` : ''} · <span className="aa-code">{code}</span></div>
                    </div>
                    <div className="aa-betrag">{b ? (b.im_teampreis ? 'im Teampreis' : euro(b.betrag)) : '…'}</div>
                  </div>
                );
              })}
              {(schonGemeldet[e.wettkaempfer_id] || []).length > 0 && (
                <div className="aa-klein">Schon angemeldet: {(schonGemeldet[e.wettkaempfer_id] || []).map((c) => deutscherName(divNach.get(c) || { Division_Name: c })).join(', ')}</div>
              )}
            </div>
          );
        })}
        <div className="aa-karte">
          <div className="aa-summe"><span>Startgeld für diese Anmeldung <Frage id="startgeld" offen={hilfe} umschalten={umschalten} /></span><span>{gesamt != null ? euro(gesamt) : '…'}</span></div>
          <HilfeBox id="startgeld" offen={hilfe} />
          <div className="aa-klein">Für {eintraege.length} Sportler, {startsGesamt} {startsGesamt === 1 ? 'Start' : 'Starts'}.{vorschau?.ohne_preis ? ' Für einige Klassen hat der Veranstalter keinen Preis eingetragen.' : ''}{vorschau?.nachmelde_aufschlag_summe > 0 ? ` Darin ${euro(vorschau.nachmelde_aufschlag_summe)} Nachmeldegebühr.` : ''}</div>
        </div>
        <div className="aa-karte">
          <h3 className="aa-zeile-q">So bezahlst du <Frage id="bezahlen" offen={hilfe} umschalten={umschalten} /></h3>
          <HilfeBox id="bezahlen" offen={hilfe} />
          {!zahlung && <p className="aa-klein">Lädt …</p>}
          {zahlung && bank && (
            <div className="aa-zahl">
              Bitte überweise{info?.zahlungsfrist ? ` bis ${info.zahlungsfrist}` : ''} an den Veranstalter:
              <div className="aa-kop"><span>Empfänger</span><code>{bank.kontoinhaber}</code></div>
              <div className="aa-kop"><span>IBAN</span><span><code>{bank.iban}</code> <button type="button" className="aa-knopf zweit mini" onClick={() => kopieren(bank.iban)}>Kopieren</button></span></div>
              {bank.bic && <div className="aa-kop"><span>BIC</span><code>{bank.bic}</code></div>}
              {zahlung.verwendungszweck && <div className="aa-kop"><span>Verwendungszweck</span><span><code>{zahlung.verwendungszweck}</code> <button type="button" className="aa-knopf zweit mini" onClick={() => kopieren(zahlung.verwendungszweck)}>Kopieren</button></span></div>}
              <div className="aa-klein">Nach dem Absenden steht unter „Anmeldungen“ auch ein QR-Code für die Banking-App.{info?.zahlungsweise ? ` ${info.zahlungsweise}` : ''}</div>
            </div>
          )}
          {zahlung && !bank && (
            <div className="aa-tipp" role="status"><span>ℹ️</span><div>
              {info?.zahlungsweise
                ? <><b>Zahlungsweise laut Ausschreibung:</b> {info.zahlungsweise}</>
                : <><b>Der Veranstalter hat seine Bankverbindung noch nicht eingetragen.</b> Du kannst trotzdem verbindlich anmelden. Bitte noch nichts überweisen: die Zahlungsdaten stehen danach unter „Anmeldungen“, sobald sie da sind, und die Rechnung kommt per E-Mail.</>}
              {(kontaktMail || kontaktTel) && <div className="aa-klein">Fragen dazu: {kontaktMail && <a href={`mailto:${kontaktMail}`}>{kontaktMail}</a>}{kontaktMail && kontaktTel ? ' · ' : ''}{kontaktTel && <a href={`tel:${String(kontaktTel).replace(/\s/g, '')}`}>{kontaktTel}</a>}</div>}
            </div></div>
          )}
        </div>
        <label className="aa-check">
          <input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} />
          <span>Die Angaben stimmen. Bei Kindern und Jugendlichen sind die Eltern mit der Teilnahme einverstanden.</span>
        </label>
        {unten(<><b>{gesamt != null ? euro(gesamt) : '…'}</b> für {eintraege.length} Sportler{!ok ? <><br />Bitte oben bestätigen</> : null}</>,
          sendet ? 'Wird angemeldet …' : 'Jetzt verbindlich anmelden', {
            zurueck: () => { setAktiv(Math.max(0, auswahlDa.length - 1)); geh(2); },
            weiter: absenden,
            aus: !ok || sendet || startsGesamt === 0,
          })}
      </main>
    );
  };

  // ── Schritt 4: Fertig ────────────────────────────────────────────────────
  const s4 = () => (
    <main className="aa-main aa-main--fertig">
      <AnmeldeBestaetigung
        daten={{ turnier: { ...turnier, turnier_id: tid }, wkIds: gesendetIds, info }}
        anmeldungen={anmeldungen}
        vereinsId={vereinsId}
        headers={headers}
        onWeitere={vonVorn}
        onUebersicht={() => onSchliessen('anmeldungen')}
      />
    </main>
  );

  return (
    <div className="aa" onKeyDown={(e) => { if (e.key === 'Escape' && hilfe) setHilfe(null); }}>
      <div className="aa-kopf">
        <div>
          <div className="aa-kopf-t">Anmeldung {turnier.name}</div>
          <div className="aa-kopf-u">{datumText(info?.datum_iso || turnier.datum_iso || turnier.datum)}{(info?.stadt || turnier.ort) ? ` · ${info?.stadt || turnier.ort}` : ''}</div>
        </div>
        <button type="button" className="aa-knopf zweit mini" onClick={() => onSchliessen()}>Schließen</button>
      </div>
      {fortschritt}
      {[s0, s1, s2, s3, s4][schritt]()}
      {hilfeKnopf}
    </div>
  );
}

